import type { AppErrorCode } from "@bt/domain";

/**
 * English source strings. Wording follows CONTEXT.md. `de.ts` must have exactly the same keys
 * (enforced by its type), and `errors` has one string per API error code (`APP_ERROR_CODES`).
 */
export const en = {
  app: {
    name: "Bimmertools Inventory",
    tagline: "Parts, vehicles and work orders in one place.",
  },
  nav: {
    overview: "Overview",
    inventory: "Inventory",
    vehicles: "Vehicles",
    workOrders: "Work orders",
    templates: "Templates",
    orders: "Orders",
    toOrder: "To order",
    settings: "Settings",
    search: "Search",
    searchHint: "Search…",
    workshop: "Workshop",
    purchasing: "Purchasing",
  },
  common: {
    save: "Save",
    saveChanges: "Save changes",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    add: "Add",
    create: "Create",
    close: "Close",
    undo: "Undo",
    remove: "Remove",
    archive: "Archive",
    unarchive: "Restore",
    archived: "Archived",
    search: "Search",
    retry: "Try again",
    optional: "optional",
    none: "None",
    all: "All",
    notes: "Notes",
    description: "Description",
    name: "Name",
    title: "Title",
    type: "Type",
    status: "Status",
    copy: "Copy",
    more: "More",
    details: "Details",
    documents: "Documents",
    links: "Links",
    total: "Total",
    price: "Price",
    quantity: "Quantity",
    created: "Created",
    deleteConfirmTitle: "Delete {{name}}?",
    deleteConfirmBody: "This can't be undone.",
    select: "Select…",
    view: "View",
  },
  validation: {
    required: "Required",
    tooLong: "Too long",
    tooLarge: "Too large",
    number: "Enter a number",
    positive: "Must be more than 0",
    nonNegative: "Can't be negative",
    nonZero: "Can't be 0",
    date: "Enter a valid date",
    url: "Enter a web address starting with http:// or https://",
    year: "Enter a year between 1885 and 2100",
    taxRate: "Enter a rate between 0 and 100 %",
    taxRates: "Each day can have only one tax rate, and there must be one base rate",
    untilBeforeFrom: "Can't be before the start date",
    email: "Enter an email address",
    oneTarget: "Choose either an order or a supplier",
    passwordTooShort: "At least 8 characters",
    passwordsDiffer: "The passwords don't match",
    pin: "4 to 8 digits",
    pinsDiffer: "The PINs don't match",
    emailTwice: "Each email only once",
    inviteDays: "Enter 1 to 90 days",
  },
  errors: {
    NOT_FOUND: "This item no longer exists.",
    UNAUTHORIZED: "Your session expired. Restart the app.",
    FORBIDDEN: "That isn't allowed here.",
    GARAGE_NOT_FOUND: "This garage doesn't exist.",
    GARAGE_REQUIRED: "Open a garage first.",
    GARAGE_NAME_INVALID: "A garage name needs 3 to 50 letters, digits or hyphens.",
    GARAGE_NAME_RESERVED: "Garage names can't start with a reserved word such as demo or bmw.",
    GARAGE_NAME_TAKEN: "There's already a garage with this name.",
    PART_IN_USE: "This part is used by work orders, templates or orders. Archive it instead.",
    PART_LINK_INVALID: "Only a genuine part can be linked with an OE or aftermarket part.",
    PART_LINK_TAKEN: "This part is already linked to another genuine part.",
    LOCATION_CYCLE: "A location can't be inside itself.",
    CHASSIS_CODE_EXISTS: "This chassis code already exists.",
    MOVEMENT_NOT_UNDOABLE: "This change can only be undone from its order or work order.",
    WORK_ORDER_COMPLETED: "This work order is completed. Reopen it to make changes.",
    WORK_ORDER_NOT_COMPLETED: "This work order isn't completed.",
    ORDER_NOT_EDITABLE: "This order can't be changed any more.",
    ORDER_INVALID_STATUS: "That isn't possible in the order's current status.",
    ORDER_HAS_RECEIPTS: "Parts of this order have already arrived. Undo the receipts first.",
    QTY_BELOW_RECEIVED: "You can't order less than has already arrived.",
    FILE_TOO_LARGE: "The file is too large (50 MB max).",
    FILE_TYPE_NOT_ALLOWED: "This file type isn't allowed here.",
    IMPORT_INVALID: "This file isn't a valid backup.",
    PACKAGE_INVALID: "This file isn't a demo package.",
    PACKAGE_TOO_NEW:
      "This demo package was made with a newer version of Bimmertools Inventory. Update the app to import it.",
    PACKAGE_TOO_OLD: "This demo package is too old for this version of the app.",
    SIGN_IN_FAILED: "Email or password is wrong.",
    WRONG_PASSWORD: "The password is wrong.",
    ACCOUNT_DISABLED: "Your account has been disabled.",
    ACCOUNT_DELETED: "Your account was deleted.",
    MEMBERSHIP_DEACTIVATED: "Your access to this garage was deactivated.",
    INVITATION_INVALID: "This invitation has expired, was already used or was withdrawn.",
    RESET_LINK_INVALID: "This link has expired or was already used. Ask for a new one.",
    LAST_GARAGE_ADMIN:
      "A garage needs an active garage admin. Make someone else garage admin first.",
    LAST_PLATFORM_ADMIN: "There has to be an admin. Make someone else admin first.",
    RATE_LIMITED: "Too many attempts. Wait a few minutes and try again.",
    EMAIL_TAKEN: "There's already an account with this email. Sign in instead.",
    ALREADY_MEMBER: "This person is already a member of this garage.",
    PASSWORD_REQUIRED: "Enter a password of at least 8 characters.",
    MAIL_FAILED: "The email couldn't be sent. Try sending it again later.",
    NOT_YOURSELF: "You can't do that to your own account.",
    UPLOADS_DISABLED: "Uploading is switched off in the demo.",
    DEMO_LINK_INVALID: "This link has expired or was already used. Ask for a new one.",
    DEMO_BUSY: "The demo is busy right now. Please try again a little later.",
    PIN_WRONG: "The PIN is wrong.",
    PIN_WAIT: "Too many wrong PINs. Wait a few minutes, or ask an admin to remove your PIN.",
    RECOVERY_CODE_WRONG: "The recovery code is wrong.",
    OPERATOR_LINK_INVALID:
      "This link has expired or was already used. Make a new one on the server.",
    CORE_UNAVAILABLE: "Core can't be reached right now. The admin app keeps trying by itself.",
    generic: "Something went wrong. Please try again.",
    network: "Can't reach the app's server.",
    pageTitle: "This page couldn't be loaded",
  } satisfies Record<AppErrorCode | "generic" | "network" | "pageTitle", string>,
  units: {
    pcs_one: "{{value}} pc",
    pcs_other: "{{value}} pcs",
    pair_one: "{{value}} pair",
    pair_other: "{{value}} pairs",
    set_one: "{{value}} set",
    set_other: "{{value}} sets",
    l: "{{value}} L",
    ml: "{{value}} ml",
    m: "{{value}} m",
    cm: "{{value}} cm",
    kg: "{{value}} kg",
    g: "{{value}} g",
  },
  unitNames: {
    pcs: "Pieces",
    pair: "Pairs",
    set: "Sets",
    l: "Litres",
    ml: "Millilitres",
    m: "Metres",
    cm: "Centimetres",
    kg: "Kilograms",
    g: "Grams",
  },
  unitShort: {
    pcs: "pcs",
    pair: "pairs",
    set: "sets",
    l: "L",
    ml: "ml",
    m: "m",
    cm: "cm",
    kg: "kg",
    g: "g",
  },
  availability: {
    in_stock: "In stock",
    on_order: "On order",
    partial: "Partial",
    missing: "Missing",
    tool: "Tool",
  },
  availabilityDetail: {
    fromStock: "{{qty}} from stock",
    fromIncoming: "{{qty}} on order",
    missing: "{{qty}} missing",
  },
  readiness: {
    ready: "Ready",
    waiting: "Waiting for delivery",
    missing: "Parts missing",
  },
  readinessLong: {
    ready: "All parts are here",
    readyBody_one: "The {{count}} part this work order needs is in stock.",
    readyBody_other: "All {{count}} parts this work order needs are in stock.",
    waiting: "Waiting for delivery",
    waitingBody_one: "{{count}} part is on order. Everything else is here.",
    waitingBody_other: "{{count}} parts are on order. Everything else is here.",
    missing: "Parts missing",
    missingBody_one: "{{count}} part isn't in stock or on order yet.",
    missingBody_other: "{{count}} parts aren't in stock or on order yet.",
    draftsBody: "Everything missing is on draft orders. Place them to get the parts on the way.",
  },
  stockStatus: {
    toOrder: "To order: {{qty}}",
    onOrder: "On order",
    low: "Low",
    inStock: "In stock",
    outOfStock: "Out of stock",
  },
  workOrderStatus: {
    idea: "Idea",
    planned: "Planned",
    in_progress: "In progress",
    completed: "Completed",
    cancelled: "Cancelled",
  },
  workOrderType: {
    maintenance: "Scheduled maintenance",
    repair: "Repair",
    tuning: "Tuning",
    restoration: "Restoration",
    roadworthiness_inspection: "Roadworthiness inspection",
    other: "Other",
  },
  workOrderTypeHint: {
    maintenance:
      "Service the maker schedules, such as an Inspektion or oil change, or regular work of your own.",
    repair: "Fixing something worn or broken.",
    tuning: "Changes beyond the factory spec: performance, looks, handling.",
    restoration: "Bringing a vehicle or part back to its original condition.",
    roadworthiness_inspection:
      "The legally required inspection (HU, MOT): getting ready for it, the inspection itself, or fixing what it found. Completing it can set the next due date.",
    other: "Anything that fits none of these.",
  },
  roadworthiness: {
    next: "Next roadworthiness inspection",
    monthHint: "Due by the end of that month.",
    dueSoon: "Roadworthiness due {{when}}",
    overdue: "Roadworthiness overdue",
    overviewTitle: "Roadworthiness inspections due",
    passed: "Passed: set the next due date",
    passedHint: "Saved with the vehicle. Take it from the sticker or the inspection report.",
    setting: "Roadworthiness inspection",
    dueAsMonth: "Month and year",
    dueAsDate: "Exact date",
    dueAsHint:
      "Enter when it is due as a month, like the sticker on a plate, or as the exact date on a certificate.",
    interval: "Roadworthiness interval",
    intervalHint: "Completing a roadworthiness inspection suggests the next due date from it.",
  },
  orderStatus: {
    draft: "Draft",
    ordered: "Ordered",
    partially_received: "Partially received",
    received: "Received",
    cancelled: "Cancelled",
  },
  orderStatusGroup: {
    draft: "Collecting",
    ordered: "Awaiting delivery",
    partially_received: "Partially received",
    received: "Received",
    cancelled: "Cancelled",
  },
  partOrigin: {
    genuine: "Genuine",
    oe: "OE",
    aftermarket: "Aftermarket",
  },
  partNumberKind: {
    primary: "Primary",
    alternative: "Alternative",
    superseded: "Superseded",
    aftermarket: "Aftermarket",
  },
  movementReason: {
    initial: "Initial amount",
    adjustment: "Adjusted",
    order_receipt: "Received",
    work_order_consumption: "Used",
  },
  figures: {
    onHand: "On hand",
    onOrder: "On order",
    required: "Required",
    toOrder: "To order",
    free: "Free",
    inDraft: "In drafts",
    minStock: "Minimum stock",
    missing: "Missing",
  },
  figureHelp: {
    onHand: "Physically in your garage right now.",
    onOrder: "Ordered and not yet arrived.",
    required: "Needed by planned and in-progress work orders.",
    toOrder: "What still has to be ordered to cover work orders and minimum stock.",
    free: "On hand and not needed by any work order.",
    inDraft: "Already on draft orders that aren't placed yet.",
  },
  overview: {
    greetingMorning: "Good morning",
    greetingAfternoon: "Good afternoon",
    greetingEvening: "Good evening",
    subtitle: "Here's what's happening in your garage.",
    toOrderTile: "To order",
    toOrderTileHint_one: "{{count}} part to order",
    toOrderTileHint_other: "{{count}} parts to order",
    readyTile: "Ready to start",
    readyTileHint: "of {{total}} planned",
    arrivingTile: "On the way",
    arrivingTileHint_one: "{{count}} order",
    arrivingTileHint_other: "{{count}} orders",
    lowTile: "Low stock",
    lowTileHint: "below minimum",
    upNext: "Up next",
    upNextEmpty: "No planned work. Plan a work order to see what you need.",
    arriving: "Arriving",
    arrivingEmpty: "Nothing on the way.",
    orderedOn: "Ordered {{date}}",
    lowStock: "Below minimum stock",
    recent: "Recent stock changes",
    recentEmpty: "Stock changes will appear here.",
    welcomeTitle: "Welcome to your garage",
    welcomeBody:
      "Keep track of every part on the shelf, what each job needs and what's still to order.",
    stepVehicle: "Add a vehicle",
    stepVehicleBody: "The car or project you work on.",
    stepParts: "Add parts",
    stepPartsBody: "What's on the shelf, and what you know you'll need.",
    stepWork: "Plan a work order",
    stepWorkBody: "See instantly which parts are missing.",
    demo: "Explore with demo data",
    demoBody:
      "Two example cars with parts, work orders and orders. You can delete it all later in Settings.",
    demoPackageBody:
      "Look around with example cars, parts, work orders and orders: download the demo package, then import it here.",
    viewAll: "View all",
  },
  parts: {
    title: "Inventory",
    count_one: "{{count}} part",
    count_other: "{{count}} parts",
    new: "New part",
    edit: "Edit part",
    searchPlaceholder: "Search name or part number…",
    filterAll: "All",
    filterToOrder: "To order",
    filterMissing: "Missing",
    filterLow: "Low stock",
    filterOnOrder: "On order",
    filterInStock: "In stock",
    filterOutOfStock: "Out of stock",
    filterArchived: "Archived",
    anyLocation: "All locations",
    anyTag: "All tags",
    emptyTitle: "No parts yet",
    emptyBody:
      "Add the parts on your shelf — and the ones you know you'll need, even before you buy them.",
    noMatchTitle: "No parts match",
    noMatchBody: "Try another search or clear the filters.",
    clearFilters: "Clear filters",
    columnPart: "Part",
    columnLocation: "Location",
    columnStatus: "Status",
    name: "Name",
    namePlaceholder: "e.g. Oil filter",
    manufacturer: "Manufacturer",
    manufacturerPlaceholder: "e.g. BMW, MANN, Bosch",
    origin: "Origin",
    originNone: "Not set",
    originHint:
      "Genuine: from the vehicle maker. OE: made by the maker's supplier, in its own box. Aftermarket: from another maker.",
    allOrigins: "Any origin",
    genuinePart: "Genuine part",
    genuinePartBody: "The genuine part this one is an alternative to.",
    alternatives: "Alternatives",
    alternativesBody:
      "OE and aftermarket parts that do the same job. Each has its own stock, sources and prices.",
    noGenuinePart: "No genuine part linked yet.",
    noAlternatives: "No alternatives linked yet.",
    linkGenuine: "Link genuine part",
    linkAlternative: "Link alternative",
    linkSearch: "Search parts or numbers…",
    linkNone: "No part with a matching origin found.",
    unlink: "Unlink",
    linked: "Parts linked",
    unlinked: "Link removed",
    numbers: "Part numbers",
    numbersHint: "Spaces and dashes don't matter when searching.",
    addNumber: "Add number",
    numberPlaceholder: "11 42 7 512 300",
    unit: "Unit",
    initialQty: "Amount on hand",
    initialQtyHint: "Enter 0 if you don't have it yet.",
    minStock: "Minimum stock",
    minStockHint: "Keep at least this many on hand. Leave empty for none.",
    location: "Location",
    locationDetail: "Exact place",
    locationDetailPlaceholder: "e.g. Bin 3",
    tags: "Tags",
    addTag: "Add tag",
    newTag: "Create tag “{{name}}”",
    duplicateNumber: "{{number}} is also used by",
    created: "Part created",
    saved: "Part saved",
    deleted: "Part deleted",
    archived: "Part archived",
    restored: "Part restored",
    stock: "Stock",
    setExact: "Set exact amount",
    setExactTitle: "Set amount on hand",
    setExactBody: "Use this after counting. The difference is recorded as an adjustment.",
    adjusted: "{{name}}: {{delta}}",
    undone: "Change undone",
    increase: "Increase",
    decrease: "Decrease",
    whereUsed: "Where used",
    whereUsedEmpty: "Not used by any work order or template yet.",
    inTemplates: "In templates",
    orders: "Orders",
    ordersEmpty: "This part hasn't been ordered yet.",
    history: "History",
    historyEmpty: "No stock changes yet.",
    sources: "Where to buy",
    sourcesEmpty: "Add shops and links so you can reorder in one click.",
    addSource: "Add source",
    editSource: "Edit source",
    supplier: "Supplier",
    newSupplier: "Create supplier “{{name}}”",
    url: "Link",
    supplierPartNumber: "Supplier's part number",
    preferred: "Preferred",
    preferredHint: "Used when grouping parts into draft orders.",
    openShop: "Open shop",
    noDescription: "No description.",
    addToOrder: "Add to order",
    deleteBlocked: "This part is used elsewhere. Archive it to hide it from the inventory.",
    archivedBanner: "This part is archived and hidden from the inventory.",
  },
  vehicles: {
    title: "Vehicles",
    count_one: "{{count}} vehicle",
    count_other: "{{count}} vehicles",
    new: "New vehicle",
    edit: "Edit vehicle",
    emptyTitle: "No vehicles yet",
    emptyBody:
      "Add the cars, bikes or engine builds you work on. Work orders and service history hang off them.",
    name: "Name",
    namePlaceholder: "e.g. E46 330Ci Coupé",
    make: "Make",
    makePlaceholder: "e.g. BMW",
    model: "Model",
    modelPlaceholder: "e.g. 325i",
    variant: "Variant",
    variantPlaceholder: "e.g. Coupé",
    modelYear: "Model year",
    productionDate: "Production date",
    vin: "VIN",
    chassisCode: "Chassis code",
    engineCode: "Engine code",
    transmission: "Transmission",
    paintCode: "Paint code",
    licensePlate: "License plate",
    mileage: "Mileage",
    mileageUnit: "Unit",
    identity: "Identity",
    technical: "Technical",
    created: "Vehicle created",
    saved: "Vehicle saved",
    deleted: "Vehicle deleted",
    deleteBody: "Its work orders and service history are deleted too. Stock doesn't change.",
    openWork: "Open work",
    openWorkEmpty: "No open work orders.",
    history: "Service history",
    historyEmpty: "Completed work orders appear here with the parts that went in.",
    shortages: "Parts needed",
    shortagesBody: "Across this vehicle's planned and in-progress work orders.",
    orderMissing: "Order missing parts",
    totalSpent: "Parts spent",
    lastService: "Last service {{date}}",
    noService: "No service recorded",
    openWorkOrders_one: "{{count}} open work order",
    openWorkOrders_other: "{{count}} open work orders",
    showArchived: "Show archived",
    archivedBanner: "This vehicle is archived.",
    partsUsed_one: "{{count}} part",
    partsUsed_other: "{{count}} parts",
  },
  workOrders: {
    title: "Work orders",
    new: "New work order",
    edit: "Edit work order",
    fromTemplate: "From template",
    tabOpen: "Open",
    tabCompleted: "Completed",
    tabAll: "All",
    emptyTitle: "No work orders yet",
    emptyBody:
      "A work order is a job on one vehicle — an oil service, a VANOS rebuild, a restoration step. Add the parts it needs and see right away what's missing.",
    emptyCompleted: "Completed work orders will show up here.",
    groupInProgress: "In progress",
    groupPlanned: "Planned",
    groupIdeas: "Ideas",
    groupCompleted: "Completed",
    groupCancelled: "Cancelled",
    vehicle: "Vehicle",
    titlePlaceholder: "e.g. Oil service",
    plannedOn: "Planned for",
    created: "Work order created",
    saved: "Work order saved",
    deleted: "Work order deleted",
    deleteBody:
      "Parts already used stay deducted from stock. To return them, reopen the work order first.",
    parts: "Parts",
    partsCount_one: "{{count}} part",
    partsCount_other: "{{count}} parts",
    addPart: "Add part",
    addPartPlaceholder: "Search parts or numbers…",
    createPart: "Create part “{{name}}”",
    partsEmpty:
      "Add the parts this job needs. Parts you don't have yet are fine — they'll show up as missing.",
    tool: "Tool",
    toolHint: "Needed but not used up",
    consumable: "Used up",
    linkedInStock: "In stock instead: {{name}} · {{qty}} free",
    useInstead: "Use this one instead",
    usingInstead: "Now using {{name}}",
    orderMissing: "Order missing parts",
    complete: "Complete",
    completeTitle: "Complete work order",
    completeBody:
      "Used amounts are deducted from stock and recorded in the vehicle's service history.",
    completedOn: "Completed on",
    mileageAtCompletion: "Mileage",
    used: "Used",
    completed: "Work order completed",
    completedBanner: "Completed on {{date}}",
    completedBannerMileage: "Completed on {{date}} at {{mileage}}",
    reopen: "Reopen",
    reopenBody: "Parts used are returned to stock and the work order goes back to in progress.",
    reopened: "Work order reopened",
    saveAsTemplate: "Save as template",
    savedAsTemplate: "Template saved",
    estimatedCost: "Estimated parts cost",
    partsCost: "Parts cost",
    unpriced_one: "{{count}} part without price",
    unpriced_other: "{{count}} parts without price",
    priorityHint: "Stock goes to in-progress work first, then by planned date.",
    template: "Template",
    orderedFor: "Ordered for this work order",
    statusChanged: "Status: {{status}}",
    insufficientStock: "Not enough on hand — stock will go negative.",
    inDraft: "In draft",
    descriptionPlaceholder: "Steps, torque specs, notes…",
  },
  templates: {
    title: "Templates",
    new: "New template",
    edit: "Edit template",
    emptyTitle: "No templates yet",
    emptyBody:
      "Save recurring jobs like an oil service once, with their parts, guides and documents. Then create work orders from them in one click.",
    used_one: "Used {{count}} time",
    used_other: "Used {{count}} times",
    neverUsed: "Not used yet",
    createWorkOrder: "Create work order",
    createWorkOrderTitle: "Create work order from template",
    created: "Template created",
    saved: "Template saved",
    deleted: "Template deleted",
    workOrders: "Work orders from this template",
    partsEmpty: "Add the parts this job usually needs.",
    partsHint: "The amounts each work order created from this template needs.",
    qtyColumn: "Required per work order",
    status: "Start as",
    interval: "Service interval",
    intervalBody:
      "Optional: how often this work is due, whichever comes first. It is shown on the template; nothing is scheduled from it.",
    intervalTime: "Time",
    intervalTimeUnit: "Time unit",
    intervalDistance: "Distance",
    months: "Months",
    years: "Years",
    everyMonths_one: "every month",
    everyMonths_other: "every {{count}} months",
    everyYears_one: "every year",
    everyYears_other: "every {{count}} years",
    everyDistance: "every {{distance}}",
    intervalEither: "{{period}} or {{distance}}",
  },
  orders: {
    title: "Orders",
    new: "New order",
    edit: "Edit order",
    emptyTitle: "No orders yet",
    emptyBody:
      "Collect parts into a draft, mark it ordered, and confirm deliveries — stock updates itself.",
    name: "Name",
    namePlaceholder: "e.g. Autumn service parts",
    supplier: "Supplier",
    noSupplier: "No supplier",
    orderNumber: "Order number",
    orderedOn: "Ordered on",
    expectedOn: "Expected on",
    placedBy: "Placed by",
    receivedOn: "Received on",
    trackingUrl: "Tracking link",
    shipping: "Shipping",
    currency: "Currency",
    subtotal: "Parts",
    lines: "Items",
    linesCount_one: "{{count}} item",
    linesCount_other: "{{count}} items",
    linesEmpty: "Add parts to this order.",
    addLine: "Add part",
    ordered: "Ordered",
    received: "Received",
    unitPrice: "Unit price",
    forWorkOrder: "For",
    place: "Mark as ordered",
    placeTitle: "Mark as ordered",
    placeBody: "Parts on this order now count as on order.",
    placed: "Order marked as ordered",
    receiveAll: "Receive all",
    receive: "Receive…",
    receiveTitle: "Confirm delivery",
    receiveBody: "Enter what arrived. It's added to stock right away.",
    receiveConfirm: "Add to stock",
    receivedToast: "Delivery added to stock",
    undoReceipt: "Undo receipt",
    receipts: "Receipts",
    revertToDraft: "Back to draft",
    cancelOrder: "Cancel order",
    cancelled: "Order cancelled",
    reopenDraft: "Reopen as draft",
    created: "Order created",
    saved: "Order saved",
    deleted: "Order deleted",
    quickPurchase: "Bought in store",
    quickPurchaseTitle: "Bought in store",
    quickPurchaseBody: "Record parts you bought on the spot. They're added to stock right away.",
    quickPurchaseDefaultName: "Store purchase",
    quickPurchaseDone: "Purchase added to stock",
    purchasedOn: "Bought on",
    addToOrderTitle: "Add to order",
    addToOrderTarget: "Order",
    addToOrderAuto: "Automatic — draft per preferred supplier",
    addToOrderNew: "New order at {{name}}",
    addedToOrders_one: "Added to draft “{{name}}”",
    addedToOrders_other: "Added to {{count}} draft orders",
    newDraftName: "New order",
    overdue: "Overdue",
    arrivesIn: "Expected {{when}}",
    openShop: "Open in shop",
    deleteBody: "The order and its items are removed. Stock doesn't change.",
  },
  toOrder: {
    title: "To order",
    subtitle:
      "Everything missing for planned work and minimum stock, grouped by preferred supplier.",
    emptyTitle: "Nothing to order",
    emptyBody: "All planned work is covered by stock and orders on the way.",
    noSupplier: "No preferred supplier",
    belowMinimum: "Below minimum stock",
    alreadyInDraft: "{{qty}} already in a draft",
    addSelected: "Add to draft orders",
    addGroup: "Add to draft",
    selectAll: "Select all",
    covered: "Covered by drafts",
  },
  makePackages: {
    title: "Make packages",
    body: "Ready-made main groups and chassis codes for the makes you work on. Load any number of them; what you have added stays when you unload a package.",
    adminOnly: "Garage admins choose which packages are loaded.",
    loaded: "Loaded",
    notLoaded: "Not loaded",
    mainGroupCount_one: "{{count}} main group",
    mainGroupCount_other: "{{count}} main groups",
    chassisCodeCount_one: "{{count}} chassis code",
    chassisCodeCount_other: "{{count}} chassis codes",
    pointer: "Ready-made ones come with the loaded make packages: {{packages}}.",
    pointerNone: "No make package is loaded, so nothing ready-made is offered.",
    names: {
      munich: "Munich data package",
      zuffenhausen: "Zuffenhausen data package",
    },
    makes: {
      munich: "For BMW, BMW M, MINI and BMW Motorrad",
      zuffenhausen: "For Porsche",
    },
    variants: {
      munich:
        "Variants have their own code, e.g. E92 (M3), E36/2S (M3), G26 (i4): a part marked E92 doesn't count as fitting an E92 (M3). A part marked E46 fits every E46 body (E46/2 Coupé, E46/3 Touring …).",
      zuffenhausen:
        "Variants have their own code, e.g. 997 (GT3), 996 (Turbo): a part marked 997 doesn't count as fitting a 997 (GT3). A part marked 997 fits both phases (997.1 and 997.2); one marked 997.2 fits only the facelift.",
    },
  },
  chassisCodes: {
    title: "Chassis codes",
    add: "Add chassis code",
    create: "Create chassis code “{{name}}”",
    any: "All chassis codes",
    fits: "Fits {{code}}",
    more: "+{{count}}",
    since: "since {{year}}",
    lists: {
      bmw: "BMW",
      bmw_m: "BMW M",
      mini: "MINI",
      motorrad: "BMW Motorrad",
      porsche: "Porsche",
    },
  },
  settings: {
    members: "Members",
    users: "Users",
    title: "Settings",
    general: "General",
    garageSettings: "Garage settings",
    garageSettingsBody: "Only garage admins see and change these settings.",
    garageSettingsBodyUsers: "Only admins see and change these settings.",
    garage: "Garage",
    garageBody:
      "Language, currency, units, prices and roadworthiness inspections for everyone in this garage.",
    language: "Language",
    languageHint: "Everyone in this garage sees the app in this language.",
    languageHintAccounts:
      "The language of this garage's data: example data, BMW main groups and invitation emails. Everyone sees the app in their own language, set in their profile.",
    languageHintUsers:
      "The language of this garage's data: example data and BMW main groups. Everyone sees the app in their own language, set in their profile.",
    theme: "Appearance",
    themeSystem: "System",
    themeLight: "Light",
    themeDark: "Dark",
    currency: "Default currency",
    mileageUnit: "Default mileage unit",
    prices: "Prices",
    pricesIncl: "Incl. tax",
    pricesExcl: "Excl. tax",
    pricesHintIncl:
      "Prices are saved without tax and shown with the rate valid today. An order keeps the rate of its order date.",
    pricesHintExcl: "Prices are entered and shown without tax.",
    taxRate: "Tax rate",
    taxRateHint:
      "A change applies from its date: enter it in advance and prices switch over on that day.",
    taxRateFrom: "From {{date}}",
    taxRateBefore: "Before {{date}}",
    taxRateCurrent: "Current",
    taxRateUpcoming: "Upcoming",
    taxRateAdd: "Add change",
    taxRateEdit: "Edit tax rate",
    taxRateChange: "Tax rate change",
    taxRateValidFrom: "Valid from",
    taxRateUntil: "Until",
    taxRateUntilHint:
      "For a temporary change. From the day after, the rate before it applies again.",
    taxRateRemove: "Remove change",
    locations: "Locations",
    locationsBody: "Where parts are stored. Locations can be nested, e.g. Garage › Shelf A.",
    newLocation: "New location",
    parentLocation: "Inside",
    topLevel: "Top level",
    suppliers: "Suppliers",
    suppliersBody: "Shops you order from.",
    newSupplier: "New supplier",
    editSupplier: "Edit supplier",
    supplierKind: "Kind",
    supplierOnline: "Online shop",
    supplierLocal: "Local dealer",
    supplierEmail: "Parts service email",
    supplierEmailHint: "Parts lists can be emailed here.",
    website: "Website",
    tags: "Tags",
    tagsBody: "Group parts, e.g. Engine, Brakes, Consumables.",
    newTag: "New tag",
    mainGroupLists: {
      bmw: {
        title: "BMW main groups",
        body: "Add the groups of BMW's parts catalogue as tags, numbered like BMW part numbers (e.g. 34 Brakes). They are added in the current language and then work like any other tag.",
      },
      vw_group: {
        title: "Porsche main groups",
        body: "Add the main groups 0–9 of Porsche's parts catalogue as tags (e.g. 6 Wheels, Brakes; in a part number like 992 698 151 G it is the fourth character). They are added in the current language and then work like any other tag.",
      },
    },
    mainGroupsMissing: "Not added yet",
    addMainGroups: "Add main groups",
    mainGroupsAdded_one: "{{count}} tag added",
    mainGroupsAdded_other: "{{count}} tags added",
    mainGroupsPresent: "All main groups are already there",
    trademark: {
      munich:
        "BMW and MINI are registered trademarks of BMW AG. Bimmertools Inventory is not affiliated with BMW.",
      zuffenhausen:
        "Porsche, 911, Boxster, Cayman, Cayenne, Macan, Panamera, Taycan and Carrera are registered trademarks of Dr. Ing. h.c. F. Porsche AG. Bimmertools Inventory is not affiliated with Porsche.",
    },
    chassisCodes: "Chassis codes",
    chassisCodesBody:
      "Mark which vehicles a part or template fits, e.g. E90 or F80. Work orders then list the parts that fit their vehicle first.",
    newChassisCode: "New chassis code",
    chooseChassisCodes: "Choose chassis codes",
    chassisCodeList: "List",
    selectAll: "Select all",
    alreadyAdded: "Added",
    noChassisCodeMatch: "No chassis code matches.",
    addChassisCodes_zero: "Add chassis codes",
    addChassisCodes_one: "Add {{count}} chassis code",
    addChassisCodes_other: "Add {{count}} chassis codes",
    chassisCodesAdded_one: "{{count}} chassis code added",
    chassisCodesAdded_other: "{{count}} chassis codes added",
    templateCount_one: "{{count}} template",
    templateCount_other: "{{count}} templates",
    vehicleCount_one: "{{count}} vehicle",
    vehicleCount_other: "{{count}} vehicles",
    color: "Colour",
    chassisCodeDefaultColor: "This code's default colour",
    data: "Data",
    installation: "Installation",
    installationBody: "Everything here covers all garages of this installation.",
    backups: "Backups",
    backupsBody: "A copy of the whole app (all garages) is saved automatically once a day.",
    autoBackup: "Daily automatic backup",
    backupDir: "Backup folder",
    backupDirDefault: "App data folder",
    chooseFolder: "Choose folder…",
    backupKeep: "Keep the last",
    backupNow: "Back up now",
    backupDone: "Backup saved",
    lastBackup: "Last backup {{when}}",
    neverBackedUp: "No backup yet",
    backupsCount_one: "{{count}} backup",
    backupsCount_other: "{{count}} backups",
    recentBackups: "Recent backups",
    useDefault: "Use default",
    export: "Export everything",
    exportBody: "A single file with all garages, their images and documents.",
    import: "Restore from file…",
    importBody: "Replaces everything in this app (all garages) with the contents of an export.",
    importConfirm: "Replace all data?",
    importConfirmBody:
      "Everything currently in the app is replaced by the file's contents. Make an export first if unsure.",
    importDone: "Data restored",
    demo: "Demo data",
    demoBody: "Load example vehicles, parts, work orders and orders to explore the app.",
    loadDemo: "Load demo data",
    demoLoaded: "Demo data loaded",
    about: "About",
    version: "Version {{version}}",
    trademarks:
      "BMW, BMW M, M Performance, BMW Motorrad and MINI are registered trademarks of the BMW Group, BMW AG or BMW M GmbH. Porsche, 911, 718, Boxster, Cayman, Cayenne, Macan, Panamera, Taycan and Carrera are registered trademarks of Dr. Ing. h.c. F. Porsche AG. All rights reserved. All mentioned trademarks belong to their respective manufacturers and distributors. This app has no affiliation with these companies. The brand names are mentioned solely to help users identify the products. The manufacturers are neither associated with nor liable for the content of this app.",
    licence: "Proprietary software, all rights reserved. It contains open-source software:",
    openSourceLicences: "Open-source licences",
    dataFolder: "Data folder",
    syncWarning:
      "Your data folder is inside a cloud-synced folder. Syncing a live database can corrupt it — put backups there instead.",
    saved: "Settings saved",
    partCount_one: "{{count}} part",
    partCount_other: "{{count}} parts",
    orderCount_one: "{{count}} order",
    orderCount_other: "{{count}} orders",
  },
  currentVehicle: {
    title: "Current vehicle",
    all: "All vehicles",
    label: "Current vehicle: {{vehicle}}",
    labelAll: "Current vehicle: all vehicles",
    notice:
      "Only what's relevant for {{vehicle}} is shown: its work orders, and parts and templates that fit {{code}} or have no chassis code.",
    noticeNoCode:
      "Only the work orders and orders of {{vehicle}} are shown. It has no chassis code, so all parts and templates are shown.",
    setChassisCode: "Set its chassis code",
    showAll: "Show all",
    workOn: "Work on this vehicle",
    current: "Current vehicle",
  },
  partsList: {
    title: "Parts list",
    print: "Print list",
    body: "Choose the parts and what the list shows, then print it for the counter or email it to the dealer.",
    for: "For",
    noDealer: "No dealer",
    show: "Show",
    genuineOnly: "Genuine parts only",
    genuineOnlyHint:
      "For a dealer who sells genuine parts: OE and aftermarket parts become their linked genuine part.",
    swapped_one: "{{count}} part becomes its genuine part.",
    swapped_other: "{{count}} parts become their genuine part.",
    leftOut: "Left out, no genuine part linked: {{names}}",
    leftOutLine: "Left out: no genuine part linked",
    genuineNumber: "Genuine {{number}}",
    insteadOf: "instead of {{name}}",
    onlinePrices: "Online prices for comparison",
    onlinePricesHint: "The lowest price at your online shops, and a column for the dealer's price.",
    vehicleDetails: "Vehicle details (VIN)",
    parts: "Parts",
    selected: "{{count}} of {{total}} selected",
    onlyMissing: "Only missing",
    note: "Note",
    noteHint: "Printed at the bottom, e.g. your name and phone number.",
    copy: "Copy as text",
    copied: "Parts list copied",
    email: "Email",
    emailTo: "Opens a new email to {{email}} in your mail app. You send it yourself.",
    emailNoAddress:
      "Opens a new email in your mail app. Add the dealer's parts service email in Settings to fill in the recipient.",
    emailTooLong:
      "The list is too long for an email link and was copied instead. Paste it into the email.",
    printAction: "Print",
    subject: "Parts list – {{what}}",
    number: "Part number",
    part: "Part",
    amount: "Amount",
    online: "Online",
    dealer: "Dealer",
    onlineTotal: "Online total",
    onlinePrice: "online {{price}}",
  },
  prices: {
    inclTax: "incl. tax",
    inclTaxRate: "incl. {{rate}} tax",
    exclTax: "excl. tax",
    column: "Price",
    enteredOn: "Price from {{date}}",
    previous: "Was {{price}} on {{date}}",
    estimate: "Estimated cost",
    unpriced_one: "{{count}} part without a price",
    unpriced_other: "{{count}} parts without a price",
  },
  garages: {
    garage: "Garage",
    title: "Garages",
    current: "Garage: {{name}}",
    new: "New garage…",
    newTitle: "New garage",
    newBody: "A garage has its own parts, vehicles, work orders, templates, orders and settings.",
    rename: "Rename garage…",
    renameTitle: "Rename garage",
    renameWarning: "The address changes too: links and bookmarks to the old one stop working.",
    nameHint: "Its name is also its address: lowercase letters, digits and hyphens.",
    address: "Address",
    create: "Create garage",
    tooShort: "At least 3 characters.",
    tooLong: "At most 50 characters.",
    reserved: "Names can't start with “{{word}}”.",
    taken: "There's already a garage with this name.",
    notFound: "There's no garage “{{name}}”",
    notFoundBody: "Open one of these instead:",
    none: "There's no garage yet.",
    notYours: "“{{name}}” isn't one of your garages",
    notYoursBody: "It doesn't exist, or you aren't a member. Open one of yours:",
    noneMember: "You aren't a member of any garage yet. Ask a garage admin to invite you.",
  },
  roles: {
    admin: "Admin",
    garage_admin: "Garage admin",
    garage_user: "Garage user",
    garage_visitor: "Visitor",
  },
  roleInText: {
    admin: "an admin",
    garage_admin: "garage admin",
    garage_user: "garage user",
    garage_visitor: "visitor",
  },
  roleHelp: {
    garage_admin: "Uses the garage and manages its members and garage settings.",
    garage_user: "Uses the garage.",
    garage_visitor: "Tries the app in a demo garage of their own.",
  },
  userStatus: {
    active: "Active",
    disabled: "Disabled",
    deleted: "Deleted account",
  },
  memberStatus: {
    active: "Active",
    deactivated: "Deactivated",
    invited: "Invited until {{date}}",
    expired: "Invitation expired",
  },
  account: {
    email: "Email",
    password: "Password",
    firstName: "First name",
    lastName: "Last name",
    newPassword: "New password",
    repeatPassword: "Repeat the password",
    passwordHint: "At least 8 characters. A few words make a good one.",
    backToSignIn: "Back to sign-in",
    backToGarages: "Back to garages",
    signInTitle: "Sign in",
    signInBody: "Welcome back to Bimmertools Inventory.",
    signIn: "Sign in",
    forgot: "Forgot password?",
    forgotTitle: "Forgot your password?",
    forgotBody: "Enter your email and you'll get a link to choose a new one.",
    sendLink: "Send link",
    sentTitle: "Check your email",
    sentBody:
      "If there's an account for {{email}}, a link to choose a new password is on its way. It works for one hour.",
    resetTitle: "Choose a new password",
    resetBody: "This signs you out on all your devices.",
    savePassword: "Save password",
    resetDone: "Password changed. Sign in with your new password.",
    inviteGarageTitle: "Join “{{garage}}”",
    inviteAdminTitle: "Run Bimmertools Inventory",
    invitedBy: "{{inviter}} invited you as {{role}}.",
    invitedAs: "You're invited as {{role}}.",
    validUntil: "The invitation is valid until {{date}}.",
    existingAccount: "There's already an account for {{email}}. Sign in to accept the invitation.",
    signedInAs: "You're signed in as {{email}}.",
    accept: "Accept invitation",
    signInAndAccept: "Sign in and accept",
    invalidTitle: "This invitation can't be used",
    invalidBody:
      "It has expired, was already accepted or was withdrawn. Ask whoever invited you to send a new one.",
    nameAndPicture: "Name and picture",
    preferences: "Language and appearance",
    preferencesBody: "Used on every device you sign in on.",
    profileTitle: "Profile",
    profileBody: "Your name and picture show to the other members of your garages.",
    profileBodyUsers: "Your name and picture show in the user picker and in the history.",
    emailHint: "You sign in with this email. It can't be changed yet.",
    createdOn: "Account created",
    lastSignIn: "Last sign-in",
    uploadPicture: "Upload picture",
    removePicture: "Remove picture",
    profileSaved: "Profile saved",
    pictureSaved: "Picture saved",
    changePassword: "Change password",
    changePasswordBody: "Other devices are signed out when you change it.",
    currentPassword: "Current password",
    passwordChanged: "Password changed",
    deleteTitle: "Delete my account",
    deleteBody:
      "Deletes your account and personal data at once: name, email, picture, password, memberships and invitations sent to you. What you entered in garages (parts, orders …) belongs to the garage and stays. This can't be undone.",
    deleteConfirmTitle: "Delete your account for good?",
    deletePassword: "Enter your password to confirm",
    deleteConfirm: "Delete my account",
    deleted: "Your account was deleted.",
    disabledTitle: "Your account has been disabled",
    disabledBody:
      "An admin disabled it, so you can't open any garage right now. Ask them if that's a mistake. You can still delete your account.",
    deletedTitle: "Your account was deleted",
    deletedBody:
      "An admin removed you from Bimmertools Inventory. Your sign-in still exists: delete it to erase everything, or accept a new invitation to come back.",
    menu: "Account",
    profile: "Profile",
    admin: "Admin",
    signOut: "Sign out",
    switchUser: "Switch user",
  },
  welcome: {
    step: "Welcome · step {{step}} of {{steps}}",
    languageTitle: "Which language would you like?",
    languageBody:
      "The app uses it on every device you sign in on. You can change it in your profile.",
    themeTitle: "How should the app look?",
    themeBody: "Light, dark, or like your device. You can change this in your profile too.",
    garageTitle: "Your demo garage",
    garageBody: "It's yours alone and is deleted with everything in it on {{date}}.",
    exampleData: "With example data",
    exampleDataBody: "Parts, vehicles, work orders and orders, to try everything right away.",
    empty: "Empty",
    emptyBody: "Start with your own parts and vehicles. Reset demo data fills it later.",
    continue: "Continue",
    back: "Back",
    finish: "Get started",
    create: "Create my demo garage",
  },
  demo: {
    requestTitle: "Try Bimmertools Inventory",
    requestBody:
      "Get a demo garage of your own, with example parts, vehicles and work orders or empty, and try everything. It's deleted with everything in it {{duration}} after you open the link.",
    hours_one: "{{count}} hour",
    hours_other: "{{count}} hours",
    minutes_one: "{{count}} minute",
    minutes_other: "{{count}} minutes",
    email: "Your email",
    send: "Send me the link",
    stored: "Your email is only used to send you the link, and is deleted with your demo garage.",
    privacy: "Privacy",
    honeypot: "Leave this field empty",
    sentTitle: "Check your inbox",
    sentBody:
      "A link to your demo garage is on its way to {{email}}. It works once, for one hour. If you already have a demo garage, the link leads back into it.",
    otherEmail: "Use another email",
    ended: "Your demo garage was deleted. Thanks for trying Bimmertools Inventory!",
    visitor: "Demo visitor",
    banner: "Demo",
    deletedIn: "deleted in {{hours}} h",
    reset: "Reset demo data",
    resetTitle: "Start again with the example data?",
    resetBody:
      "Everything in this demo garage is replaced with the example data, so your changes are lost.",
    resetDone: "The example data is back.",
    end: "End demo now",
    endTitle: "End the demo now?",
    endBody:
      "Your demo garage is deleted with everything in it, and you're signed out. This can't be undone.",
    profileBody: "You're trying Bimmertools Inventory in a demo garage of your own.",
    deletedOn: "Your demo garage is deleted on {{date}}.",
  },
  members: {
    title: "Members",
    body: "Who can open this garage, and who manages it.",
    invite: "Invite…",
    inviteTitle: "Invite someone",
    inviteBody: "They get an email with a link to join this garage.",
    role: "Role",
    send: "Send invitation",
    invited: "Invitation sent to {{email}}",
    invitations: "Invitations",
    invitedBy: "Invited by {{name}}",
    resend: "Send again",
    resent: "Invitation sent again",
    revoke: "Withdraw",
    revoked: "Invitation withdrawn",
    you: "you",
    lastSignIn: "Last sign-in {{when}}",
    neverSignedIn: "Hasn't signed in yet",
    accountDisabled: "Account disabled",
    actions: "Actions for {{name}}",
    makeGarageAdmin: "Make garage admin",
    makeGarageUser: "Make garage user",
    deactivate: "Deactivate",
    reactivate: "Reactivate",
    remove: "Remove from garage…",
    removeTitle: "Remove {{name}} from “{{garage}}”?",
    removeBody: "They can't open this garage any more. What they entered stays.",
    empty: "No members yet.",
  },
  users: {
    title: "Users",
    body: "Who uses the app on this computer.",
    offTitle: "Several people use this computer?",
    pointPicker: "Everyone picks themselves when the app opens, with a PIN if they like.",
    pointOwn: "Each keeps their own language, appearance and picture.",
    pointHistory: "The stock history and orders show who did what.",
    pinNote:
      "A PIN keeps people apart, not intruders out: anyone who can use this computer can read the app's data.",
    firstAdmin: "You'll be the first admin and add the others.",
    switchOn: "Switch on users",
    switchOnAgain: "Switch on users again",
    kept_one: "{{count}} user from before is kept.",
    kept_other: "{{count}} users from before are kept.",
    switchOff: "Switch off users…",
    switchOffConfirm: "Switch off",
    switchOffTitle: "Switch off users?",
    switchOffBody:
      "The app opens for one person again, without the user picker. The users are kept for when you switch them on again.",
    add: "Add user…",
    addTitle: "Add a user",
    addBody:
      "They pick themselves when the app opens and choose their language, appearance and an optional PIN.",
    admin: "Admin",
    adminHelp: "Adds and deletes users and changes the garage settings.",
    user: "User",
    added: "{{name}} added",
    hasPin: "PIN",
    noPin: "No PIN",
    makeAdmin: "Make admin",
    removeAdmin: "Remove admin role",
    removePin: "Remove PIN",
    pinRemoved: "PIN removed. {{name}} can choose a new one in their profile.",
    delete: "Delete user…",
    deleteTitle: "Delete {{name}}?",
    deleteBody:
      "They no longer appear in the user picker. What they did stays in the history, as done by a former user.",
    deleted: "{{name}} deleted",
    formerUser: "Former user",
    recoveryTitle: "Your recovery code",
    recoveryBody:
      "Write it down and keep it somewhere safe. If someone forgets their PIN and no admin can remove it, the code removes it, once: then there's a new one.",
    recoveryDone: "I've written it down",
    recoveryCode: "Recovery code",
    forgotPin: "Forgot your PIN?",
    forgotPinBody:
      "Ask an admin to remove it. If no admin can, enter the recovery code that was shown when users were switched on.",
    recover: "Remove PIN and open",
    pickerTitle: "Who's using the app?",
    pickerBody: "Choose yourself.",
    enterPin: "Enter your PIN.",
    open: "Open",
    someoneElse: "Someone else",
    pin: "PIN",
    newPin: "New PIN",
    repeatPin: "Repeat the PIN",
    pinHint: "4 to 8 digits.",
    choosePinTitle: "Choose a PIN?",
    choosePinBody:
      "Optional: with a PIN, only you can open your profile on this computer. It keeps people apart, not intruders out.",
    savePin: "Save PIN",
    pinWith: "The user picker asks for it before your profile opens.",
    pinWithout: "Without a PIN, anyone using this computer can open your profile.",
    setPin: "Set PIN",
    changePin: "Change PIN",
    pinSaved: "PIN saved",
    ownPinRemoved: "PIN removed",
  },
  admin: {
    title: "Admin",
    garages: "Garages",
    users: "Users",
    settings: "Settings",
    backToGarages: "Back to garages",
    notAllowedTitle: "This is for admins",
    notAllowedBody: "Your account can't open the admin interface.",
    garagesBody: "Every garage of this service, with its members.",
    newGarage: "New garage…",
    newGarageTitle: "New garage",
    newGarageBody:
      "Name it, and invite its first users if you like. Without an invited garage admin, you become its garage admin.",
    firstUsers: "Invite people (optional)",
    addPerson: "Add person",
    removePerson: "Remove person",
    createAndInvite: "Create and send invitations",
    create: "Create garage",
    garageCreated: "Garage created, invitations sent",
    garageCreatedAlone: "Garage created",
    unsent: "These invitations couldn't be sent: {{emails}}. Send them again on the garage's page.",
    membersCount_one: "{{count}} member",
    membersCount_other: "{{count}} members",
    garageAdminsCount_one: "{{count}} garage admin",
    garageAdminsCount_other: "{{count}} garage admins",
    invitedCount_one: "{{count}} invited",
    invitedCount_other: "{{count}} invited",
    noGarageAdmin: "No garage admin",
    createdOn: "Created {{date}}",
    openGarage: "Open garage",
    renameGarage: "Rename…",
    noGarages: "No garages yet.",
    usersBody: "Everyone with an account, and the garages they're in.",
    search: "Search name or email",
    noUsers: "No users found.",
    noGarage: "No garage",
    you: "You",
    lastSignIn: "Last sign-in",
    created: "Created",
    never: "Never",
    makeAdmin: "Make admin",
    removeAdmin: "Remove admin role",
    disable: "Disable",
    enable: "Enable",
    delete: "Delete…",
    deleteTitle: "Delete {{name}}?",
    deleteBody:
      "They're removed from all garages and their profile is cleared. They can still sign in to see that, and delete their sign-in themselves. A new invitation makes them a user again.",
    userActions: "Actions for {{name}}",
    settingsBody: "Settings for the whole service.",
    inviteDays: "Invitations are valid for",
    days: "days",
    inviteDaysHint: "1 to 90 days. Applies to invitations sent from now on.",
    saved: "Saved",
  },
  demoPackage: {
    title: "Demo package",
    body: "Example data to explore the app. Importing replaces this garage's data; other garages stay as they are.",
    download: "Download demo package",
    downloadBody: "One file per language.",
    import: "Import demo package…",
    importBody: "Load a downloaded demo package into this garage.",
    importConfirm: "Replace data",
    confirm: "Replace everything in “{{garage}}”?",
    confirmBody:
      "Its parts, vehicles, work orders, templates, orders, tags, chassis codes, locations, suppliers, images, documents and settings are replaced by the demo package.",
    backupFirst: "A backup of the app is saved first.",
    madeWith: "Made with version {{version}} on {{date}}",
    workOrderCount_one: "{{count}} work order",
    workOrderCount_other: "{{count}} work orders",
    tooNew: "Made with version {{version}}: update Bimmertools Inventory to import it.",
    imported: "Demo package imported",
    export: "Export demo package",
    exportBody: "This garage as a demo package, marked as {{language}}.",
  },
  palette: {
    placeholder: "Search parts, numbers, vehicles, work orders…",
    actions: "Actions",
    navigation: "Go to",
    parts: "Parts",
    vehicles: "Vehicles",
    workOrders: "Work orders",
    orders: "Orders",
    empty: "Nothing found.",
    onHand: "{{qty}} on hand",
  },
  attachments: {
    addImage: "Add image",
    dropHere: "Drop files here or click to upload",
    dropImages: "Drop images here, paste, or click to upload",
    uploading: "Uploading…",
    uploaded: "Uploaded",
    makeCover: "Use as cover",
    download: "Download",
  },
  links: {
    add: "Add link",
    url: "Web address",
    title: "Title",
    titleHint: "Leave empty to use the page's title.",
  },
  markdown: {
    write: "Write",
    preview: "Preview",
    hint: "Markdown supported",
    empty: "Nothing to preview.",
  },
  adminApp: {
    title: "Bimmertools Admin",
    pages: "Pages",
    nav: {
      overview: "Overview",
      health: "Health",
      passkeys: "Passkeys",
    },
    installation: "Installation",
    installations: {
      app: "Production",
      staging: "Staging",
      demo: "Public demo",
    },
    range: "Range",
    ranges: {
      "30d": "30 days",
      "90d": "90 days",
      "12m": "12 months",
      all: "All",
    },
    language: "Language",
    themeLight: "Switch to light",
    themeDark: "Switch to dark",
    signOut: "Sign out",
    coreUnavailable: {
      title: "Core can't be reached",
      body: "The figures come back as soon as it answers; the admin app keeps trying.",
    },
    passkeyFailed: "The passkey didn't work. Try again, or use another passkey.",
    signIn: {
      title: "Sign in",
      body: "Operators sign in with a passkey. There is no password.",
      email: "Email",
      emailHint: "Your passkey is suggested here",
      button: "Sign in with a passkey",
    },
    register: {
      title: "Create your passkey",
      body: "This link from the server works once. Your device asks you to create a passkey for the admin app; after that you sign in with it.",
      button: "Create passkey",
    },
    passkeys: {
      body: "Sign in on another device by adding a passkey for it here. A passkey you no longer have can go.",
      firstName: "First passkey",
      defaultName: "Passkey",
      name: "Name of the new passkey",
      nameHint: "e.g. MacBook, iPad",
      add: "Add passkey",
      added: "Passkey added",
      created: "Added {{when}}",
      empty: "No passkeys.",
      delete: "Delete {{name}}",
      deleteTitle: "Delete the passkey {{name}}?",
      deleteBody: "It no longer signs you in. Your other passkeys stay.",
      deleteLast:
        "It is your only passkey: without it you need a new link from the server (operator-link) to sign in again.",
      deleted: "Passkey deleted",
    },
    states: {
      up: "Up",
      down: "Down",
      internal_only: "Only inside",
      unknown: "No status yet",
    },
    overview: {
      installations: "Installations",
      noInstallations: "No installation has reported to core yet.",
      state: "State",
      version: "Version",
      uptime: "Up for",
      report: "Last report",
      noReport: "No report yet",
      keyFigures: "Key figures: {{installation}}",
      keyFiguresHint:
        "Counts at the end of each month, sums for work orders and spend; changes compare with 30 days and 12 months ago.",
      noFigures: "No reports yet.",
      figuresNeedDatabase: "Key figures come back when core's database answers again.",
      figure: "Figure",
      currency: "Currency",
      trend: "{{figure}} over 12 months: from {{from}} to {{to}}",
      over30Days: "over 30 days",
      over12Months: "over 12 months",
    },
    figures: {
      garages_active30: "Active garages (30 days)",
      users_active30: "Active users (30 days)",
      garages_total: "Garages",
      parts_total: "Parts",
      workOrders_completed: "Work orders completed (30 days)",
      spend: "Spend (last month)",
    },
    alerts: {
      title: "Alerts",
      none: "No alerts.",
      severity: "Severity",
      kind: "Kind",
      message: "Message",
      critical: "Critical",
      warning: "Warning",
      misuse: "Misuse",
      down: "{{installation}} is down.",
      internal_only:
        "{{installation}} answers inside the server, but not through its public address.",
      restarted: "{{installation}} restarted in the last 24 hours.",
      backup_old: "The last backup is {{value}} old (more than {{threshold}}).",
      backup_old_none: "There is no backup yet.",
      offsite_old: "The last off-site copy is {{value}} old (more than {{threshold}}).",
      offsite_old_none: "There is no off-site copy yet.",
      certificate_expiring_one: "The certificate of {{installation}} expires in {{value}} day.",
      certificate_expiring_other: "The certificate of {{installation}} expires in {{value}} days.",
      disk_full: "The disk is {{value}} full (alert from {{threshold}}).",
      swap_used: "{{value}} of the swap is in use (alert from {{threshold}}).",
      memory_high: "{{installation}} uses {{value}} of its memory limit.",
      server_errors:
        "{{value}} of the requests to {{installation}} failed with a server error in the last hour.",
      unexpected_error_one: "{{installation}} had {{value}} unexpected error in the last 24 hours.",
      unexpected_error_other:
        "{{installation}} had {{value}} unexpected errors in the last 24 hours.",
      core_database_down: "Core's database is down; figures aren't kept until it is back.",
      demo_at_cap_one: "The public demo is full: {{value}} demo garage.",
      demo_at_cap_other: "The public demo is full: {{value}} demo garages.",
      failed_signins_one: "{{value}} failed sign-in on {{installation}} in the last hour.",
      failed_signins_other: "{{value}} failed sign-ins on {{installation}} in the last hour.",
      rate_limited_one: "{{value}} rate-limit hit on {{installation}} in the last hour.",
      rate_limited_other: "{{value}} rate-limit hits on {{installation}} in the last hour.",
      refused_one: "{{value}} refused request (401 or 403) on {{installation}} in the last hour.",
      refused_other:
        "{{value}} refused requests (401 and 403) on {{installation}} in the last hour.",
      demo_requests_one: "{{value}} demo request in a day, far more than the demos opened.",
      demo_requests_other: "{{value}} demo requests in a day, far more than the demos opened.",
      invitations_one: "A garage on {{installation}} sent {{value}} invitation today.",
      invitations_other: "A garage on {{installation}} sent {{value}} invitations today.",
      uploads: "A garage on {{installation}} uploaded {{value}} today.",
      release_errors:
        "{{subject}} fails more since the new version of {{installation}}: {{value}} of its calls.",
      release_slower:
        "{{subject}} is slower since the new version of {{installation}}: p95 {{value}}, before {{threshold}}.",
    },
    server: {
      title: "Server",
      na: "n/a",
      disk: "Disk",
      memory: "Memory",
      swap: "Swap",
      load: "Load",
      loadPeriods: "1, 5 and 15 min",
      used: "{{used}} of {{total}} used ({{share}})",
      available: "{{available}} of {{total}} available",
      backups: "Backups",
      dump: "Database dump",
      offsite: "Off-site copy",
      never: "never",
      notConfigured: "not configured",
      certificates: "Certificates",
      daysLeft: "{{days}} days left",
      core: "Core",
      upFor: "up for {{duration}}",
      databaseUp: "Database up",
      databaseDown: "Database down",
    },
    health: {
      availability: "Availability",
      traffic: "Traffic",
      errors: "Errors",
      resources: "Resources",
      period: "Period",
      hour: "Hour",
      last48Hours: "The last 48 hours",
      last48HoursHint: "Each hour by core's probes inside the server: up, partly down or down.",
      stripLabel:
        "The last 48 hours: {{up}} up, {{partial}} partly down, {{down}} down, {{none}} without data.",
      strip: {
        up_one: "{{count}} hour up",
        up_other: "{{count}} hours up",
        partial_one: "{{count}} hour partly down",
        partial_other: "{{count}} hours partly down",
        down_one: "{{count}} hour down",
        down_other: "{{count}} hours down",
        none_one: "{{count}} hour without data",
        none_other: "{{count}} hours without data",
      },
      upInside: "Up inside",
      upOutside: "Up outside",
      latency: "Response time",
      latencyHint: "Of all requests: the median and the slowest 5 % (p95).",
      median: "Median",
      requests: "Requests",
      serverErrors: "Server errors",
      serverErrorsHint: "The share of requests answered with a 5xx status.",
      perVersion: "Per version",
      perVersionHint: "tRPC calls, unexpected errors and p95 per app version over the range.",
      calls: "Calls",
      unexpected: "Unexpected errors",
      unexpectedHint: "Failures nobody planned for; never their message.",
      noCalls: "No calls in this range.",
      recentErrors: "The latest unexpected errors",
      recentErrorsHint: "Since the server started: where, the error's class and the SQL state.",
      when: "When",
      where: "Where",
      errorClass: "Class",
      sqlState: "SQL state",
      noErrors: "No unexpected errors since the server started.",
      friction: "Where people get stuck",
      frictionHint: "The error codes users ran into, per procedure: refusals, not failures.",
      procedure: "Procedure",
      code: "Code",
      count: "Count",
      noFriction: "Nobody ran into an error code in this range.",
      slowAndFailing: "Slow and failing procedures",
      slowAndFailingHint: "The slowest by p95; below, those with unexpected errors.",
      failing: "With unexpected errors",
      ofCalls: "of {{calls}} calls",
      slowRequests: "Requests over 2.5 s",
      memory: "Memory",
      memoryHint: "The highest use in each period, against the container's limit.",
      peakMemory: "Peak",
      memoryLimit: "Limit",
      eventLoop: "Event loop delay",
      eventLoopHint: "The longest the server was busy before it could answer, per period.",
      database: "Database",
      databaseHint: "{{size}} in all, {{connections}} connections; the largest tables:",
      table: "Table",
      size: "Size",
      rows: "Rows",
      noTables: "No tables reported.",
      files: "Files",
      filesHint: "{{total}} uploaded, {{deduped}} stored; per garage, and the largest:",
      garage: "Garage",
      noFiles: "No files yet.",
      catalogue: "Parts catalogue",
      catalogueParts: "Catalogue parts",
      lastImport: "Last import",
      mail: "Mail",
      mailHint: "Emails sent per kind over the range; failures apart.",
      mailKind: "Kind",
      sent: "Sent",
      failed: "Failed",
      mailFailed_one: "{{count}} failed",
      mailFailed_other: "{{count}} failed",
      mailKinds: {
        invitation: "Invitations",
        password_reset: "Password resets",
        demo_link: "Demo links",
      },
      noMail: "No emails in this range.",
    },
    chart: {
      showTable: "Show as table",
      showChart: "Show as chart",
      exportCsv: "Export CSV",
      keysHint: "{{label}}. The arrow keys move through the periods.",
      distribution:
        "Median {{median}}, middle half {{p25}}–{{p75}}, p90 {{p90}}, max {{max}}, mean {{mean}}; over {{n}}, {{none}} with none.",
      maxAt: "max {{max}}",
      noValues: "No values yet.",
    },
    format: {
      partOf: "{{part}} of {{base}}",
      daysHours: "{{days}} d {{hours}} h",
      hoursMinutes: "{{hours}} h {{minutes}} min",
      minutes: "{{minutes}} min",
    },
  },
};

export type Translation = DeepStrings<typeof en>;

type DeepStrings<T> = { [K in keyof T]: T[K] extends string ? string : DeepStrings<T[K]> };
